import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../alert/AlertContext";
import { withLocalErrorHandling } from "../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../forms/useFormikWithDirtyForm";
import { useClientNoteApi } from "../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../query/clientFileQueryKeys";
import { ClientNoteFormValues } from "../ClientNoteView.types";
import { createClientNoteSchema } from "../ClientNoteView.validation";

export function useClientNote(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const clientNoteApi = useClientNoteApi();

  const noteQuery = useQuery({
    queryKey: [ClientFileQueryKey.ClientNote, clientId],
    queryFn: () => clientNoteApi.getClientNote({ clientId }, withLocalErrorHandling),
  });

  const saveMutation = useMutation({
    mutationFn: (values: ClientNoteFormValues) =>
      clientNoteApi.updateClientNote({ clientId, updateClientNoteRequest: { note: values.note } }),
    onSuccess: (saved) => {
      queryClient.setQueryData([ClientFileQueryKey.ClientNote, clientId], saved);
      showSuccess(t("clients.client.clientNote.saveSuccess"));
    },
  });

  const validationSchema = useMemo(() => createClientNoteSchema(t), [t]);

  const formik = useFormikWithDirtyForm<ClientNoteFormValues>({
    initialValues: { note: noteQuery.data?.note ?? null },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await saveMutation.mutateAsync(values);
    },
  });

  return { formik, isPending: noteQuery.isPending, isError: noteQuery.isError };
}
